/**
 * VoiceChannelJoinAnimation - Animated component for joining voice channels
 * Features the Iris logo with rainbow effects and divine messaging
 */

import { useEffect, useState, useRef } from 'react';

interface VoiceChannelJoinAnimationProps {
  username: string;
  channelName: string;
  onComplete?: () => void;
}

export default function VoiceChannelJoinAnimation({
  username,
  channelName,
  onComplete,
}: VoiceChannelJoinAnimationProps) {
  const [stage, setStage] = useState<'entering' | 'connected' | 'fading'>('entering');
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    // Stage 1: Entering (1s)
    const enterTimer = setTimeout(() => {
      setStage('connected');
    }, 1000);

    // Stage 2: Connected message (1s)
    const connectedTimer = setTimeout(() => {
      setStage('fading');
    }, 2000);

    // Stage 3: Fade out and complete (0.4s)
    const completeTimer = setTimeout(() => {
      onCompleteRef.current?.();
    }, 2400);

    // Safety fallback: ensure dismissal after 3.5s maximum
    const safetyTimer = setTimeout(() => {
      onCompleteRef.current?.();
    }, 3500);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCompleteRef.current?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      clearTimeout(enterTimer);
      clearTimeout(connectedTimer);
      clearTimeout(completeTimer);
      clearTimeout(safetyTimer);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleDismiss = () => {
    onCompleteRef.current?.();
  };

  return (
    <div
      onClick={handleDismiss}
      className={`fixed inset-0 z-50 flex items-center justify-center bg-brand-background/80 backdrop-blur-sm transition-opacity duration-500 cursor-pointer ${
        stage === 'fading' ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
      title="Click anywhere to dismiss"
    >
      <div className="relative">
        {/* Rainbow circle expanding effect */}
        <div className="absolute inset-0 -m-20 flex items-center justify-center">
          <div
            className={`rounded-full transition-all duration-1000 ${
              stage === 'entering' ? 'w-0 h-0 opacity-0' : 'w-96 h-96 opacity-30'
            }`}
            style={{
              background:
                'conic-gradient(from 0deg, var(--brand-secondary), var(--brand-outline), var(--brand-tertiary), var(--color-success), var(--brand-primary), var(--brand-secondary))',
              filter: 'blur(30px)',
            }}
          />
        </div>

        {/* Golden pulse rings */}
        <div className="absolute inset-0 -m-16 flex items-center justify-center">
          <div className="w-80 h-80 rounded-full border-4 border-brand-primary/40 animate-pulse-slow" />
        </div>
        <div className="absolute inset-0 -m-12 flex items-center justify-center">
          <div
            className="w-72 h-72 rounded-full border-4 border-brand-primary/30 animate-pulse-slow"
            style={{ animationDelay: '0.3s' }}
          />
        </div>

        {/* Central content */}
        <div className="text-center px-12 py-10 max-w-md relative z-10">
          {/* Iris logo with float animation */}
          <div className="mb-6 flex justify-center">
            <div className="w-32 h-32 relative">
              {/* Spinning rainbow halo */}
              <div className="absolute inset-0 animate-spin-slow">
                <div
                  className="w-full h-full rounded-full opacity-40"
                  style={{
                    background:
                      'conic-gradient(from 0deg, var(--brand-secondary), var(--brand-outline), var(--brand-tertiary), var(--color-success), var(--brand-primary), var(--brand-secondary))',
                    filter: 'blur(8px)',
                  }}
                />
              </div>

              {/* Logo */}
              <div className="absolute inset-2 animate-float">
                <img
                  src="/irus-logo-color.svg"
                  alt="Iris"
                  className="w-full h-full object-contain drop-shadow-lg"
                />
              </div>
            </div>
          </div>

          {/* Message */}
          <div className="space-y-3">
            {stage === 'entering' && (
              <div className="animate-fade-in">
                <h2 className="text-2xl font-bold text-brand-primary mb-2">⚡ Swift Passage ⚡</h2>
                <p className="text-brand-on-background/90">
                  <span className="font-semibold">{username}</span> is descending to the realm...
                </p>
              </div>
            )}

            {stage === 'connected' && (
              <div className="animate-fade-in">
                <h2 className="text-2xl font-bold text-green-500 mb-2">🌈 Bridge Established 🌈</h2>
                <p className="text-brand-on-background/90">
                  Connected to{' '}
                  <span className="font-semibold text-brand-tertiary">#{channelName}</span>
                </p>
                <p className="text-sm text-brand-secondary/70 mt-2">
                  The divine messenger has linked heaven and earth
                </p>
              </div>
            )}
          </div>

          {/* Particle effects */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-lg">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 rounded-full animate-float"
                style={{
                  background: ['#9333ea', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#f43f5e'][
                    i % 6
                  ],
                  left: `${10 + i * 12}%`,
                  top: `${20 + (i % 3) * 25}%`,
                  animationDelay: `${i * 0.3}s`,
                  animationDuration: `${4 + (i % 3)}s`,
                  opacity: 0.6,
                }}
              />
            ))}
          </div>
        </div>

        {/* Bottom sparkle trail */}
        <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="w-3 h-3 rounded-full animate-pulse"
              style={{
                background: ['#9333ea', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b'][i],
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
